import { Box, Typography } from '@mui/material';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TrackedLink as Link } from '../../../../../components/TrackedComponents';
import useProfile from '../../../../../hooks/profile';
import palette from '../../../../ColorPalette';
import { RTLDirectionContext } from '../../../../Layout';
import { Modes } from '../SetupTool';
import MeasurementImage from '../assets/measurement-image.svg';
import SideCameraSketch from '../assets/side-camera-sketch.svg';

const INTRO: Partial<Record<Modes, string[]>> = {
    install: ['install_intro1', 'install_intro2'],
    switch: ['switch_intro1', 'switch_intro2', 'switch_intro3'],
    adjust: ['switch_intro1', 'switch_intro2', 'switch_intro3'],
};

interface InstallationParamsIntroProps {
    mode: Modes;
}

const InstallationParamsIntro: React.FC<InstallationParamsIntroProps> = (props) => {
    const [showBSDGuide, setShowBSDGuide] = useState(false);
    const { t } = useTranslation();
    const { profile } = useProfile();
    const isRTL = useContext(RTLDirectionContext);
    const expectedCameras = profile.customer.settings.installed_cameras;

    const hasRightBSD = expectedCameras.includes('bsd_right');
    const hasLeftBSD = expectedCameras.includes('bsd_left');
    const hasBSD = hasLeftBSD || hasRightBSD;

    if (showBSDGuide) {
        return (
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                {/**1 to 4*/}
                {Array.from({ length: 4 }, (_, idx) => (
                    <Typography key={idx} sx={{ fontWeight: 500, mt: 1, width: '100%' }}>
                        {t(`setup_tool.installation_params.bsd_guide_instruction_${idx + 1}`)}
                    </Typography>
                ))}
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: isRTL ? 'row-reverse' : 'row',
                        justifyContent: 'center',
                        py: 3,
                        gap: 2,
                    }}
                >
                    {hasLeftBSD ? <img src={SideCameraSketch} style={{ transform: 'scaleX(-1)' }} width={150} /> : null}
                    {hasRightBSD ? <img src={SideCameraSketch} width={150} /> : null}
                </Box>
            </Box>
        );
    }

    return props.mode in INTRO ? (
        <Box
            sx={{
                width: '100%',
                aspectRatio: '4 / 3',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <img src={MeasurementImage} width={200} />
            {INTRO[props.mode]?.map((item) => (
                <Typography key={item} sx={{ fontWeight: 500, mt: 1, width: '100%' }}>
                    {t(`setup_tool.installation_params.${item}`)}
                </Typography>
            ))}
            {expectedCameras.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                    <Typography sx={{ fontWeight: 500, mt: 1, width: '100%' }}>
                        {t('setup_tool.installation_params.expected_cameras', { cameras: expectedCameras.length })}
                    </Typography>
                    <ol style={{ fontWeight: 500 }}>
                        {expectedCameras.map((camera) => (
                            <li key={camera}>
                                <Typography sx={{ fontWeight: 500 }}>
                                    {t(`setup_tool.device_feedback.cameras.${camera}`)}
                                </Typography>
                            </li>
                        ))}
                    </ol>
                </Box>
            ) : null}
            {hasBSD ? (
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        minHeight: 0,
                        flexShrink: 0,
                        my: 1,
                    }}
                >
                    <Link id="bsd-installation-guide" component="button" onClick={() => setShowBSDGuide(true)}>
                        <Typography fontSize={14} sx={{ color: palette.neutral[700] }}>
                            {t('setup_tool.installation_params.bsd_guide_link')}
                        </Typography>
                    </Link>
                </Box>
            ) : null}
        </Box>
    ) : null;
};

export default InstallationParamsIntro;
